import { formatRupiah, MOVEMENT_LABELS, type StockMovementType } from '@safepos/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  type Column,
  DataTable,
  Modal,
  PageHeader,
  SelectField,
  TextField,
} from '@safepos/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';
import { useInvalidate, type ProductDto } from './CatalogPages';
import { OutletPicker } from './OutletConfigPages';
import { type RepackRecord, RepackDialog, useRepackHistory } from './RepackDialog';

const num = (s: string) => Number(s.replace(',', '.'));
const fmtQty = (q: number) =>
  new Intl.NumberFormat('id-ID', { maximumFractionDigits: 3 }).format(q);
const date = (s: string | null) =>
  s ? new Date(s).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' }) : '—';

interface VariantOpt {
  id: string;
  label: string;
  unit: string;
  tracked: boolean;
  sku: string | null;
  barcode: string | null;
  price: number;
}
/** Semua varian aktif sebagai pilihan (nama gabungan produk + varian). */
function useVariants() {
  const q = useQuery({
    queryKey: ['products', 'variant-options'],
    queryFn: () => api.get<{ data: ProductDto[] }>('/products?limit=200'),
  });
  const list: VariantOpt[] = (q.data?.data ?? []).flatMap((p) =>
    p.variants.map((v) => ({
      id: v.id,
      label: p.type === 'variant' ? `${p.name} — ${v.name}` : p.name,
      unit: p.unit,
      tracked: p.trackStock,
      sku: v.sku,
      barcode: v.barcode,
      price: v.price,
    })),
  );
  return list;
}
function VariantSelect({
  value,
  onChange,
  options,
  label = 'Barang',
}: {
  value: string;
  onChange: (v: string) => void;
  options: VariantOpt[];
  label?: string;
}) {
  return (
    <SelectField label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Pilih barang…</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
          {o.sku ? ` (${o.sku})` : ''}
        </option>
      ))}
    </SelectField>
  );
}

// ======================= STOK =======================
interface Level {
  variantId: string;
  name: string;
  sku: string | null;
  unit: string;
  qty: number;
  minQty: number;
  avgCost: number;
  contentQty: number | null;
  price: number;
  value: number;
  low: boolean;
  negative: boolean;
}
interface Movement {
  id: string;
  type: StockMovementType;
  qty: number;
  unitCost: number | null;
  note: string | null;
  at: string;
}

export function StockPage() {
  const { can, me } = useAuth();
  const inv = useInvalidate();
  const write = can('inventory:write');
  const canRepack = write && me?.features?.repack !== false;
  const [repack, setRepack] = useState<{ sourceId?: string } | null>(null);
  const [outletId, setOutletId] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | 'low' | 'negative'>('all');
  const levels = useQuery({
    queryKey: ['stock-levels', outletId, q, status],
    enabled: !!outletId,
    queryFn: () =>
      api.get<{
        data: Level[];
        totals: { items: number; value: number; low: number; negative: number };
      }>(
        `/stock-levels?outletId=${outletId}&status=${status}&limit=500${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      ),
  });
  const [adj, setAdj] = useState<{ l: Level; qty: string; reason: string } | null>(null);
  const [min, setMin] = useState<{ l: Level; v: string } | null>(null);
  const [hist, setHist] = useState<Level | null>(null);
  const history = useQuery({
    queryKey: ['stock-movements', outletId, hist?.variantId],
    enabled: !!hist,
    queryFn: () =>
      api.get<{ data: Movement[] }>(
        `/stock-movements?outletId=${outletId}&variantId=${hist!.variantId}&limit=100`,
      ),
  });
  const saveAdj = useSubmit(async () => {
    await api.post('/stock-adjustments', {
      outletId,
      variantId: adj!.l.variantId,
      qty: num(adj!.qty),
      reason: adj!.reason.trim(),
    });
    inv('stock-levels', 'stock-movements');
    setAdj(null);
  });
  const saveMin = useSubmit(async () => {
    await api.put('/stock-levels/min', {
      outletId,
      variantId: min!.l.variantId,
      minQty: num(min!.v || '0'),
    });
    inv('stock-levels');
    setMin(null);
  });
  const repacks = useRepackHistory(outletId, canRepack || can('inventory:read'));
  const t = levels.data?.totals;
  const cols: Column<Level & { id: string }>[] = [
    { header: 'Barang', cell: (l) => <span className="font-medium">{l.name}</span> },
    { header: 'SKU', cell: (l) => l.sku ?? '—' },
    {
      header: 'Stok',
      cell: (l) => (
        <span
          className={
            l.negative ? 'font-semibold text-red-600' : l.low ? 'font-semibold text-amber-600' : ''
          }
        >
          {fmtQty(l.qty)} {l.unit}
          {l.negative && <Badge tone="danger">Negatif</Badge>}
          {l.low && !l.negative && <Badge tone="warning">Rendah</Badge>}
        </span>
      ),
    },
    { header: 'Min', cell: (l) => (l.minQty ? fmtQty(l.minQty) : '—') },
    { header: 'Modal rata-rata', cell: (l) => formatRupiah(l.avgCost) },
    { header: 'Nilai', cell: (l) => formatRupiah(Math.max(0, l.value)) },
    {
      header: '',
      className: 'text-right',
      cell: (l) => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setHist(l)}>
            Riwayat
          </Button>
          {write && (
            <>
              <Button variant="secondary" onClick={() => setMin({ l, v: String(l.minQty || '') })}>
                Min
              </Button>
              {canRepack && l.qty > 0 && (
                <Button variant="secondary" onClick={() => setRepack({ sourceId: l.variantId })}>
                  Pecah
                </Button>
              )}
              <Button variant="secondary" onClick={() => setAdj({ l, qty: '', reason: '' })}>
                Sesuaikan
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Stok"
        subtitle="Saldo per outlet = jumlah seluruh pergerakan (ledger). Aktifkan “Lacak stok” di produk."
        actions={
          canRepack ? (
            <Button variant="secondary" disabled={!outletId} onClick={() => setRepack({})}>
              Pecah stok
            </Button>
          ) : undefined
        }
      />
      {t && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {(
            [
              ['Barang terlacak', t.items],
              ['Nilai stok', formatRupiah(t.value)],
              ['Stok rendah', t.low],
              ['Stok negatif', t.negative],
            ] as const
          ).map(([k, v]) => (
            <Card key={k}>
              <div className="text-sm text-slate-600">{k}</div>
              <div className="text-2xl font-semibold">{v}</div>
            </Card>
          ))}
        </div>
      )}
      {t && t.negative > 0 && (
        <div className="mb-3">
          <Alert tone="info">
            {t.negative} barang bernilai negatif — biasanya akibat penjualan offline sebelum stok
            masuk tercatat. Lakukan penerimaan/opname untuk meluruskan.
          </Alert>
        </div>
      )}
      <Card actions={<OutletPicker value={outletId} onChange={setOutletId} />}>
        <div className="mb-3 flex flex-wrap gap-3">
          <div className="w-64">
            <TextField
              label="Cari"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="nama / SKU / barcode"
            />
          </div>
          <div className="w-48">
            <SelectField
              label="Tampilkan"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <option value="all">Semua</option>
              <option value="low">Rendah & negatif</option>
              <option value="negative">Negatif saja</option>
            </SelectField>
          </div>
        </div>
        <DataTable
          columns={cols}
          rows={(levels.data?.data ?? []).map((l) => ({ ...l, id: l.variantId }))}
        />
      </Card>

      {(repacks.data?.length ?? 0) > 0 && (
        <div className="mt-4">
          <Card title="Riwayat pecah stok">
            <ul className="divide-y text-sm">
              {repacks.data!.map((r: RepackRecord) => (
                <li key={r.id} className="py-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">
                      {fmtQty(r.source.qty)} × {r.source.name} →{' '}
                      {r.outputs.map((o) => `${fmtQty(o.qty)} × ${o.name}`).join(', ')}
                    </span>
                    <span className="text-xs text-slate-500">{date(r.at)}</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    Modal dipindahkan {formatRupiah(r.costTotal)}
                    {r.lossContent != null && r.lossContent > 0
                      ? ` · susut ${fmtQty(r.lossContent)}`
                      : ''}
                    {r.note ? ` · ${r.note}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
      {repack && (
        <RepackDialog
          outletId={outletId}
          rows={levels.data?.data ?? []}
          sourceId={repack.sourceId}
          onClose={() => setRepack(null)}
          onDone={() => {
            inv('stock-levels', 'stock-movements', 'stock-repacks');
            setRepack(null);
          }}
        />
      )}
      <Modal open={!!adj} title={`Penyesuaian stok · ${adj?.l.name}`} onClose={() => setAdj(null)}>
        {adj && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void saveAdj.run();
            }}
          >
            {saveAdj.error && <Alert>{saveAdj.error}</Alert>}
            <p className="text-sm text-slate-600">
              Stok saat ini {fmtQty(adj.l.qty)} {adj.l.unit}. Isi negatif untuk mengurangi
              (rusak/hilang).
            </p>
            <TextField
              label="Perubahan (+/−)"
              inputMode="decimal"
              value={adj.qty}
              onChange={(e) => setAdj({ ...adj, qty: e.target.value })}
              required
              autoFocus
            />
            <TextField
              label="Alasan"
              value={adj.reason}
              onChange={(e) => setAdj({ ...adj, reason: e.target.value })}
              required
              minLength={3}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setAdj(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={saveAdj.busy || !num(adj.qty)}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
      <Modal open={!!min} title={`Stok minimum · ${min?.l.name}`} onClose={() => setMin(null)}>
        {min && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void saveMin.run();
            }}
          >
            {saveMin.error && <Alert>{saveMin.error}</Alert>}
            <TextField
              label={`Peringatan bila stok ≤ (${min.l.unit})`}
              inputMode="decimal"
              value={min.v}
              onChange={(e) => setMin({ ...min, v: e.target.value })}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setMin(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={saveMin.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
      <Modal open={!!hist} title={`Riwayat · ${hist?.name}`} onClose={() => setHist(null)}>
        <ul className="max-h-[60vh] divide-y overflow-auto text-sm">
          {(history.data?.data ?? []).map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-3 py-2">
              <span>
                <b>{MOVEMENT_LABELS[m.type] ?? m.type}</b>
                <span className="block text-xs text-slate-500">
                  {date(m.at)}
                  {m.note ? ` · ${m.note}` : ''}
                </span>
              </span>
              <span className={m.qty < 0 ? 'text-red-600' : 'text-emerald-700'}>
                {m.qty > 0 ? '+' : ''}
                {fmtQty(m.qty)}
              </span>
            </li>
          ))}
          {history.data?.data.length === 0 && (
            <li className="py-6 text-center text-slate-500">Belum ada pergerakan.</li>
          )}
        </ul>
      </Modal>
    </>
  );
}

// ======================= SUPPLIER & PO =======================
interface Supplier {
  id: string;
  name: string;
  phone: string | null;
  contact: string | null;
  terms: string | null;
  note: string | null;
}
interface Po {
  id: string;
  number: string;
  outletId: string;
  supplierName: string | null;
  status: 'draft' | 'ordered' | 'partial' | 'received' | 'cancelled';
  total: number;
  createdAt: string;
  items: {
    id: string;
    variantId: string;
    name: string | null;
    qtyOrdered: number;
    qtyReceived: number;
    unitCost: number;
  }[];
}
const poTone = {
  draft: 'neutral',
  ordered: 'info',
  partial: 'warning',
  received: 'success',
  cancelled: 'danger',
} as const;
const poText = {
  draft: 'Draft',
  ordered: 'Dipesan',
  partial: 'Sebagian',
  received: 'Selesai',
  cancelled: 'Batal',
};

export function PurchasingPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const write = can('inventory:write');
  const variants = useVariants();
  const suppliers = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<Supplier[]>('/suppliers'),
  });
  const pos = useQuery({
    queryKey: ['pos-list'],
    queryFn: () => api.get<Po[]>('/purchase-orders?limit=100'),
  });

  const [sup, setSup] = useState<Partial<Supplier> | null>(null);
  const saveSup = useSubmit(async () => {
    const body = {
      name: sup!.name,
      phone: sup!.phone || null,
      contact: sup!.contact || null,
      terms: sup!.terms || null,
    };
    if (sup!.id) await api.patch(`/suppliers/${sup!.id}`, body);
    else await api.post('/suppliers', body);
    inv('suppliers');
    setSup(null);
  });
  const delSup = useSubmit(async (s: Supplier) => {
    if (!confirm(`Hapus supplier ${s.name}?`)) return;
    await api.del(`/suppliers/${s.id}`);
    inv('suppliers');
  });

  const [form, setForm] = useState<{
    outletId: string;
    supplierId: string;
    note: string;
    rows: { variantId: string; qty: string; cost: string }[];
  } | null>(null);
  const create = useSubmit(async () => {
    await api.post('/purchase-orders', {
      outletId: form!.outletId,
      supplierId: form!.supplierId,
      note: form!.note.trim() || null,
      items: form!.rows
        .filter((r) => r.variantId)
        .map((r) => ({
          variantId: r.variantId,
          qty: num(r.qty),
          unitCost: Math.round(num(r.cost || '0')),
        })),
    });
    inv('pos-list');
    setForm(null);
  });

  const [detail, setDetail] = useState<Po | null>(null);
  const [recv, setRecv] = useState<Record<string, string>>({});
  const act = useSubmit(async (path: string, body: unknown = {}) => {
    const r = await api.post<Po>(path, body);
    setDetail(r);
    setRecv({});
    inv('pos-list', 'stock-levels');
  });

  const poCols: Column<Po>[] = [
    {
      header: 'No. PO',
      cell: (p) => (
        <button className="font-medium text-brand-700 underline" onClick={() => setDetail(p)}>
          {p.number}
        </button>
      ),
    },
    { header: 'Supplier', cell: (p) => p.supplierName },
    { header: 'Tanggal', cell: (p) => date(p.createdAt) },
    { header: 'Total', cell: (p) => formatRupiah(p.total) },
    { header: 'Status', cell: (p) => <Badge tone={poTone[p.status]}>{poText[p.status]}</Badge> },
  ];
  const supCols: Column<Supplier>[] = [
    { header: 'Supplier', cell: (s) => <span className="font-medium">{s.name}</span> },
    { header: 'Telepon', cell: (s) => s.phone ?? '—' },
    { header: 'Termin', cell: (s) => s.terms ?? '—' },
    {
      header: '',
      className: 'text-right',
      cell: (s) =>
        write && (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setSup(s)}>
              Ubah
            </Button>
            <Button variant="ghost" onClick={() => void delSup.run(s)}>
              Hapus
            </Button>
          </div>
        ),
    },
  ];
  const outlets = useQuery({
    queryKey: ['outlets'],
    queryFn: () => api.get<{ id: string; name: string }[]>('/outlets'),
  });

  return (
    <>
      <PageHeader
        title="Pembelian"
        subtitle="Supplier, Purchase Order, dan penerimaan barang (sebagian/penuh) dengan modal rata-rata bergerak"
        actions={
          write && (
            <Button
              onClick={() =>
                setForm({
                  outletId: outlets.data?.[0]?.id ?? '',
                  supplierId: suppliers.data?.[0]?.id ?? '',
                  note: '',
                  rows: [{ variantId: '', qty: '1', cost: '' }],
                })
              }
            >
              + Purchase Order
            </Button>
          )
        }
      />
      {delSup.error && <Alert>{delSup.error}</Alert>}
      <Card title="Purchase Order">
        <DataTable columns={poCols} rows={pos.data ?? []} />
      </Card>
      <div className="mt-4">
        <Card
          title="Supplier"
          actions={write && <Button onClick={() => setSup({ name: '' })}>+ Supplier</Button>}
        >
          <DataTable columns={supCols} rows={suppliers.data ?? []} />
        </Card>
      </div>

      <Modal
        open={!!sup}
        title={sup?.id ? 'Ubah supplier' : 'Tambah supplier'}
        onClose={() => setSup(null)}
      >
        {sup && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void saveSup.run();
            }}
          >
            {saveSup.error && <Alert>{saveSup.error}</Alert>}
            <TextField
              label="Nama"
              value={sup.name ?? ''}
              onChange={(e) => setSup({ ...sup, name: e.target.value })}
              required
              autoFocus
            />
            <TextField
              label="Telepon"
              value={sup.phone ?? ''}
              onChange={(e) => setSup({ ...sup, phone: e.target.value })}
            />
            <TextField
              label="Kontak"
              value={sup.contact ?? ''}
              onChange={(e) => setSup({ ...sup, contact: e.target.value })}
            />
            <TextField
              label="Termin pembayaran"
              value={sup.terms ?? ''}
              onChange={(e) => setSup({ ...sup, terms: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setSup(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={saveSup.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!form} title="Purchase Order baru" onClose={() => setForm(null)}>
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void create.run();
            }}
          >
            {create.error && <Alert>{create.error}</Alert>}
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField
                label="Outlet penerima"
                value={form.outletId}
                onChange={(e) => setForm({ ...form, outletId: e.target.value })}
              >
                {outlets.data?.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label="Supplier"
                value={form.supplierId}
                onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
              >
                {suppliers.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </SelectField>
            </div>
            {form.rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_6rem_8rem] items-end gap-2">
                <VariantSelect
                  options={variants}
                  value={r.variantId}
                  label={i === 0 ? 'Barang' : ''}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      rows: form.rows.map((x, j) => (j === i ? { ...x, variantId: v } : x)),
                    })
                  }
                />
                <TextField
                  label={i === 0 ? 'Jumlah' : ''}
                  inputMode="decimal"
                  value={r.qty}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      rows: form.rows.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)),
                    })
                  }
                />
                <TextField
                  label={i === 0 ? 'Harga beli' : ''}
                  inputMode="numeric"
                  value={r.cost}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      rows: form.rows.map((x, j) =>
                        j === i ? { ...x, cost: e.target.value.replace(/\D/g, '') } : x,
                      ),
                    })
                  }
                />
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                setForm({ ...form, rows: [...form.rows, { variantId: '', qty: '1', cost: '' }] })
              }
            >
              + Baris
            </Button>
            <TextField
              label="Catatan"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Batal
              </Button>
              <Button
                type="submit"
                disabled={
                  create.busy ||
                  !form.supplierId ||
                  !form.rows.some((r) => r.variantId && num(r.qty) > 0)
                }
              >
                Simpan draft
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!detail} title={`PO ${detail?.number ?? ''}`} onClose={() => setDetail(null)}>
        {detail && (
          <div className="space-y-3">
            {act.error && <Alert>{act.error}</Alert>}
            <div className="flex items-center justify-between text-sm">
              <span>{detail.supplierName}</span>
              <Badge tone={poTone[detail.status]}>{poText[detail.status]}</Badge>
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th>Barang</th>
                  <th>Pesan</th>
                  <th>Terima</th>
                  <th>Harga</th>
                  {(detail.status === 'ordered' || detail.status === 'partial') && (
                    <th>Terima sekarang</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {detail.items.map((i) => (
                  <tr key={i.id}>
                    <td className="py-1">{i.name}</td>
                    <td>{fmtQty(i.qtyOrdered)}</td>
                    <td>{fmtQty(i.qtyReceived)}</td>
                    <td>{formatRupiah(i.unitCost)}</td>
                    {(detail.status === 'ordered' || detail.status === 'partial') && (
                      <td>
                        <input
                          aria-label={`Terima ${i.name}`}
                          className="w-24 rounded border px-2 py-1"
                          inputMode="decimal"
                          placeholder={fmtQty(i.qtyOrdered - i.qtyReceived)}
                          value={recv[i.id] ?? ''}
                          onChange={(e) => setRecv({ ...recv, [i.id]: e.target.value })}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-right text-sm">Total {formatRupiah(detail.total)}</div>
            {write && (
              <div className="flex flex-wrap justify-end gap-2">
                {detail.status === 'draft' && (
                  <Button onClick={() => void act.run(`/purchase-orders/${detail.id}/order`)}>
                    Pesan ke supplier
                  </Button>
                )}
                {(detail.status === 'draft' ||
                  (detail.status === 'ordered' &&
                    detail.items.every((i) => i.qtyReceived === 0))) && (
                  <Button
                    variant="ghost"
                    onClick={() => void act.run(`/purchase-orders/${detail.id}/cancel`)}
                  >
                    Batalkan PO
                  </Button>
                )}
                {(detail.status === 'ordered' || detail.status === 'partial') && (
                  <Button
                    disabled={act.busy}
                    onClick={() => {
                      const items = detail.items
                        .map((i) => ({
                          itemId: i.id,
                          qty:
                            recv[i.id] === undefined || recv[i.id] === ''
                              ? i.qtyOrdered - i.qtyReceived
                              : num(recv[i.id]!),
                        }))
                        .filter((x) => x.qty > 0);
                      void act.run(`/purchase-orders/${detail.id}/receive`, { items });
                    }}
                  >
                    Terima barang
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

// ======================= OPNAME & TRANSFER =======================
interface OpRow {
  id: string;
  status: string;
  note: string | null;
  createdAt: string;
  itemCount: number;
  countedCount: number;
  outletId: string;
}
interface OpDetail {
  id: string;
  status: 'draft' | 'applied' | 'cancelled';
  note: string | null;
  items: {
    variantId: string;
    name: string;
    unit: string;
    system: number;
    counted: number | null;
    applied: number | null;
  }[];
}
interface TrRow {
  id: string;
  status: string;
  fromOutletId: string;
  toOutletId: string;
  createdAt: string;
  itemCount: number;
  note: string | null;
}
interface TrDetail {
  id: string;
  status: 'draft' | 'sent' | 'received' | 'cancelled';
  fromOutletId: string;
  toOutletId: string;
  items: { variantId: string; name: string; unit: string; qty: number; received: number | null }[];
}
const stTone: Record<string, 'neutral' | 'info' | 'success' | 'danger' | 'warning'> = {
  draft: 'neutral',
  sent: 'info',
  applied: 'success',
  received: 'success',
  cancelled: 'danger',
};
const stText: Record<string, string> = {
  draft: 'Draft',
  sent: 'Dikirim',
  applied: 'Diterapkan',
  received: 'Diterima',
  cancelled: 'Batal',
};

export function StockOpsPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const write = can('inventory:write');
  const variants = useVariants();
  const outlets = useQuery({
    queryKey: ['outlets'],
    queryFn: () => api.get<{ id: string; name: string }[]>('/outlets'),
  });
  const oname = (id: string) => outlets.data?.find((o) => o.id === id)?.name ?? '—';
  const ops = useQuery({
    queryKey: ['opnames'],
    queryFn: () => api.get<OpRow[]>('/stock-opnames'),
  });
  const trs = useQuery({
    queryKey: ['transfers'],
    queryFn: () => api.get<TrRow[]>('/stock-transfers'),
  });

  // opname
  const [newOp, setNewOp] = useState<{ outletId: string; note: string } | null>(null);
  const [op, setOp] = useState<OpDetail | null>(null);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const startOp = useSubmit(async () => {
    const r = await api.post<OpDetail>('/stock-opnames', {
      outletId: newOp!.outletId,
      note: newOp!.note.trim() || null,
    });
    inv('opnames');
    setNewOp(null);
    setCounts({});
    setOp(r);
  });
  const openOp = useSubmit(async (id: string) => {
    setCounts({});
    setOp(await api.get<OpDetail>(`/stock-opnames/${id}`));
  });
  const saveCounts = useSubmit(async (apply: boolean) => {
    const items = Object.entries(counts)
      .filter(([, v]) => v !== '')
      .map(([variantId, v]) => ({ variantId, counted: num(v) }));
    let cur = op!;
    if (items.length) cur = await api.put<OpDetail>(`/stock-opnames/${op!.id}/items`, { items });
    if (apply) cur = await api.post<OpDetail>(`/stock-opnames/${op!.id}/submit`);
    setOp(cur);
    setCounts({});
    inv('opnames', 'stock-levels');
  });
  const cancelOp = useSubmit(async () => {
    setOp(await api.post<OpDetail>(`/stock-opnames/${op!.id}/cancel`));
    inv('opnames');
  });

  // transfer
  const [newTr, setNewTr] = useState<{
    from: string;
    to: string;
    rows: { variantId: string; qty: string }[];
  } | null>(null);
  const [tr, setTr] = useState<TrDetail | null>(null);
  const [got, setGot] = useState<Record<string, string>>({});
  const createTr = useSubmit(async () => {
    const r = await api.post<TrDetail>('/stock-transfers', {
      fromOutletId: newTr!.from,
      toOutletId: newTr!.to,
      items: newTr!.rows
        .filter((x) => x.variantId)
        .map((x) => ({ variantId: x.variantId, qty: num(x.qty) })),
    });
    inv('transfers');
    setNewTr(null);
    setTr(r);
  });
  const openTr = useSubmit(async (id: string) => {
    setGot({});
    setTr(await api.get<TrDetail>(`/stock-transfers/${id}`));
  });
  const trAct = useSubmit(async (action: 'send' | 'receive' | 'cancel') => {
    const body =
      action === 'receive'
        ? {
            items: Object.entries(got)
              .filter(([, v]) => v !== '')
              .map(([variantId, v]) => ({ variantId, qty: num(v) })),
          }
        : {};
    setTr(await api.post<TrDetail>(`/stock-transfers/${tr!.id}/${action}`, body));
    setGot({});
    inv('transfers', 'stock-levels');
  });

  const opCols: Column<OpRow>[] = [
    {
      header: 'Tanggal',
      cell: (o) => (
        <button className="text-brand-700 underline" onClick={() => void openOp.run(o.id)}>
          {date(o.createdAt)}
        </button>
      ),
    },
    { header: 'Outlet', cell: (o) => oname(o.outletId) },
    { header: 'Dihitung', cell: (o) => `${o.countedCount}/${o.itemCount}` },
    { header: 'Status', cell: (o) => <Badge tone={stTone[o.status]}>{stText[o.status]}</Badge> },
  ];
  const trCols: Column<TrRow>[] = [
    {
      header: 'Tanggal',
      cell: (t) => (
        <button className="text-brand-700 underline" onClick={() => void openTr.run(t.id)}>
          {date(t.createdAt)}
        </button>
      ),
    },
    { header: 'Dari → Ke', cell: (t) => `${oname(t.fromOutletId)} → ${oname(t.toOutletId)}` },
    { header: 'Barang', cell: (t) => t.itemCount },
    { header: 'Status', cell: (t) => <Badge tone={stTone[t.status]}>{stText[t.status]}</Badge> },
  ];

  return (
    <>
      <PageHeader
        title="Opname & Transfer"
        subtitle="Hitung fisik dan sesuaikan selisih; pindahkan stok antar outlet (draft → dikirim → diterima)"
      />
      {(openOp.error || openTr.error) && <Alert>{openOp.error ?? openTr.error}</Alert>}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          title="Stok opname"
          actions={
            write && (
              <Button onClick={() => setNewOp({ outletId: outlets.data?.[0]?.id ?? '', note: '' })}>
                + Opname
              </Button>
            )
          }
        >
          <DataTable columns={opCols} rows={ops.data ?? []} />
        </Card>
        <Card
          title="Transfer stok"
          actions={
            write && (
              <Button
                onClick={() =>
                  setNewTr({
                    from: outlets.data?.[0]?.id ?? '',
                    to: outlets.data?.[1]?.id ?? '',
                    rows: [{ variantId: '', qty: '1' }],
                  })
                }
              >
                + Transfer
              </Button>
            )
          }
        >
          <DataTable columns={trCols} rows={trs.data ?? []} />
        </Card>
      </div>

      <Modal open={!!newOp} title="Opname baru" onClose={() => setNewOp(null)}>
        {newOp && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void startOp.run();
            }}
          >
            {startOp.error && <Alert>{startOp.error}</Alert>}
            <SelectField
              label="Outlet"
              value={newOp.outletId}
              onChange={(e) => setNewOp({ ...newOp, outletId: e.target.value })}
            >
              {outlets.data?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Catatan"
              value={newOp.note}
              onChange={(e) => setNewOp({ ...newOp, note: e.target.value })}
            />
            <p className="text-xs text-slate-500">
              Semua barang terlacak stok akan dimasukkan ke daftar hitung.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setNewOp(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={startOp.busy}>
                Mulai
              </Button>
            </div>
          </form>
        )}
      </Modal>
      <Modal open={!!op} title="Opname" onClose={() => setOp(null)}>
        {op && (
          <div className="space-y-3">
            {(saveCounts.error || cancelOp.error) && (
              <Alert>{saveCounts.error ?? cancelOp.error}</Alert>
            )}
            <Badge tone={stTone[op.status]}>{stText[op.status]}</Badge>
            <div className="max-h-[50vh] overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th>Barang</th>
                    <th>Sistem</th>
                    <th>Hitung</th>
                    <th>Selisih</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {op.items.map((i) => {
                    const cur = counts[i.variantId] ?? (i.counted == null ? '' : String(i.counted));
                    const diff =
                      op.status === 'applied' ? i.applied : cur === '' ? null : num(cur) - i.system;
                    return (
                      <tr key={i.variantId}>
                        <td className="py-1">{i.name}</td>
                        <td>
                          {fmtQty(i.system)} {i.unit}
                        </td>
                        <td>
                          {op.status === 'draft' ? (
                            <input
                              aria-label={`Hitung ${i.name}`}
                              className="w-24 rounded border px-2 py-1"
                              inputMode="decimal"
                              value={cur}
                              onChange={(e) =>
                                setCounts({ ...counts, [i.variantId]: e.target.value })
                              }
                            />
                          ) : i.counted == null ? (
                            '—'
                          ) : (
                            fmtQty(i.counted)
                          )}
                        </td>
                        <td
                          className={
                            diff && diff < 0
                              ? 'text-red-600'
                              : diff && diff > 0
                                ? 'text-emerald-700'
                                : ''
                          }
                        >
                          {diff == null ? '—' : `${diff > 0 ? '+' : ''}${fmtQty(diff)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {write && op.status === 'draft' && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={() => void cancelOp.run()}>
                  Batalkan
                </Button>
                <Button
                  variant="secondary"
                  disabled={saveCounts.busy}
                  onClick={() => void saveCounts.run(false)}
                >
                  Simpan hitungan
                </Button>
                <Button
                  disabled={saveCounts.busy}
                  onClick={() =>
                    confirm('Terapkan selisih ke stok? Tindakan ini tidak dapat dibatalkan.') &&
                    void saveCounts.run(true)
                  }
                >
                  Terapkan selisih
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={!!newTr} title="Transfer stok baru" onClose={() => setNewTr(null)}>
        {newTr && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void createTr.run();
            }}
          >
            {createTr.error && <Alert>{createTr.error}</Alert>}
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField
                label="Dari outlet"
                value={newTr.from}
                onChange={(e) => setNewTr({ ...newTr, from: e.target.value })}
              >
                {outlets.data?.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label="Ke outlet"
                value={newTr.to}
                onChange={(e) => setNewTr({ ...newTr, to: e.target.value })}
              >
                {outlets.data?.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </SelectField>
            </div>
            {newTr.rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_6rem] items-end gap-2">
                <VariantSelect
                  options={variants.filter((v) => v.tracked)}
                  value={r.variantId}
                  label={i === 0 ? 'Barang' : ''}
                  onChange={(v) =>
                    setNewTr({
                      ...newTr,
                      rows: newTr.rows.map((x, j) => (j === i ? { ...x, variantId: v } : x)),
                    })
                  }
                />
                <TextField
                  label={i === 0 ? 'Jumlah' : ''}
                  inputMode="decimal"
                  value={r.qty}
                  onChange={(e) =>
                    setNewTr({
                      ...newTr,
                      rows: newTr.rows.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)),
                    })
                  }
                />
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                setNewTr({ ...newTr, rows: [...newTr.rows, { variantId: '', qty: '1' }] })
              }
            >
              + Baris
            </Button>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setNewTr(null)}>
                Batal
              </Button>
              <Button
                type="submit"
                disabled={
                  createTr.busy || newTr.from === newTr.to || !newTr.rows.some((r) => r.variantId)
                }
              >
                Simpan draft
              </Button>
            </div>
          </form>
        )}
      </Modal>
      <Modal open={!!tr} title="Transfer stok" onClose={() => setTr(null)}>
        {tr && (
          <div className="space-y-3">
            {trAct.error && <Alert>{trAct.error}</Alert>}
            <div className="flex items-center justify-between text-sm">
              <span>
                {oname(tr.fromOutletId)} → {oname(tr.toOutletId)}
              </span>
              <Badge tone={stTone[tr.status]}>{stText[tr.status]}</Badge>
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th>Barang</th>
                  <th>Kirim</th>
                  <th>Diterima</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {tr.items.map((i) => (
                  <tr key={i.variantId}>
                    <td className="py-1">{i.name}</td>
                    <td>
                      {fmtQty(i.qty)} {i.unit}
                    </td>
                    <td>
                      {tr.status === 'sent' ? (
                        <input
                          aria-label={`Diterima ${i.name}`}
                          className="w-24 rounded border px-2 py-1"
                          inputMode="decimal"
                          placeholder={fmtQty(i.qty)}
                          value={got[i.variantId] ?? ''}
                          onChange={(e) => setGot({ ...got, [i.variantId]: e.target.value })}
                        />
                      ) : i.received == null ? (
                        '—'
                      ) : (
                        <span className={i.received < i.qty ? 'text-amber-600' : ''}>
                          {fmtQty(i.received)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {write && (
              <div className="flex justify-end gap-2">
                {tr.status === 'draft' && (
                  <Button variant="ghost" onClick={() => void trAct.run('cancel')}>
                    Batalkan
                  </Button>
                )}
                {tr.status === 'draft' && (
                  <Button disabled={trAct.busy} onClick={() => void trAct.run('send')}>
                    Kirim
                  </Button>
                )}
                {tr.status === 'sent' && (
                  <Button disabled={trAct.busy} onClick={() => void trAct.run('receive')}>
                    Terima barang
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

// ======================= RESEP =======================
export function RecipesPage() {
  const { can } = useAuth();
  const write = can('inventory:write');
  const variants = useVariants();
  const [variantId, setVariantId] = useState('');
  const [rows, setRows] = useState<{ componentVariantId: string; qty: string }[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    setMsg(null);
    if (!variantId) return setRows([]);
    void api
      .get<{ componentVariantId: string; qty: number }[]>(`/variants/${variantId}/recipe`)
      .then((r) =>
        setRows(r.map((x) => ({ componentVariantId: x.componentVariantId, qty: String(x.qty) }))),
      );
  }, [variantId]);
  const save = useSubmit(async () => {
    await api.put(`/variants/${variantId}/recipe`, {
      items: rows
        .filter((r) => r.componentVariantId && num(r.qty) > 0)
        .map((r) => ({ componentVariantId: r.componentVariantId, qty: num(r.qty) })),
    });
    setMsg('Resep disimpan');
  });
  const ingredients = variants.filter((v) => v.id !== variantId);
  return (
    <>
      <PageHeader
        title="Resep (BOM)"
        subtitle="Penjualan hidangan mengurangi stok bahan baku sebesar takaran resep"
      />
      {msg && <Alert tone="info">{msg}</Alert>}
      {save.error && <Alert>{save.error}</Alert>}
      <Card>
        <div className="space-y-4">
          <VariantSelect
            options={variants}
            value={variantId}
            onChange={setVariantId}
            label="Hidangan / produk jadi"
          />
          {variantId && (
            <>
              <p className="text-xs text-slate-500">
                Bahan baku adalah produk dengan “Lacak stok” aktif (nonaktifkan produknya agar tidak
                tampil di kasir). Satu tingkat: bahan tidak boleh punya resep sendiri.
              </p>
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_8rem_auto] items-end gap-2">
                  <VariantSelect
                    options={ingredients}
                    value={r.componentVariantId}
                    label={i === 0 ? 'Bahan' : ''}
                    onChange={(v) =>
                      setRows(rows.map((x, j) => (j === i ? { ...x, componentVariantId: v } : x)))
                    }
                  />
                  <TextField
                    label={i === 0 ? 'Takaran per porsi' : ''}
                    inputMode="decimal"
                    value={r.qty}
                    onChange={(e) =>
                      setRows(rows.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))
                    }
                  />
                  <Button variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                    Hapus
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setRows([...rows, { componentVariantId: '', qty: '1' }])}
                >
                  + Bahan
                </Button>
                {write && (
                  <Button disabled={save.busy} onClick={() => void save.run()}>
                    Simpan resep
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </>
  );
}
